import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import Container from '../ui/Container';
import useFetch from '../../hooks/useFetch';
import { getProjects, imageUrl, parseImages } from '../../lib/api';

export default function FeaturedProject() {
  const { data, isLoading, error } = useFetch(getProjects, []);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (error) return null;

  // Filtrar proyectos destacados explícitamente marcados o tomar los primeros como fallback
  const featuredProjects =
    data && data.length > 0
      ? data.filter(p => p.destacado === true || p.destacado === 'true')
      : [];

  const projectsToDisplay =
    featuredProjects.length > 0
      ? featuredProjects
      : data && data.length > 0
      ? data.slice(0, 3)
      : [];

  const total = projectsToDisplay.length;

  // 0 destacados y no cargando: no renderizar caja vacía
  if (!isLoading && total === 0) {
    return null;
  }

  const activeProject = projectsToDisplay[currentIndex] || null;
  const projectImages = activeProject ? parseImages(activeProject.imagen) : [];
  const firstImageName = projectImages && projectImages.length > 0 ? projectImages[0] : '';
  const mainSrc = firstImageName ? imageUrl(firstImageName) : '';

  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? total - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev === total - 1 ? 0 : prev + 1));
  };

  return (
    <Box
      as="section"
      py={{ base: 12, md: 16 }}
      bg="surface.muted"
      aria-label="Proyectos destacados"
    >
      <Container>
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={{ base: 8, lg: 12 }}
          alignItems="center"
        >
          {/* Imagen principal */}
          {isLoading || !activeProject ? (
            <Skeleton borderRadius="lg" h={{ base: '260px', lg: '380px' }} />
          ) : (
            <Box position="relative" borderRadius="lg" overflow="hidden">
              <Image
                src={mainSrc}
                alt={activeProject.nombre}
                borderRadius="lg"
                w="100%"
                h={{ base: '260px', lg: '380px' }}
                objectFit="cover"
                loading="lazy"
                decoding="async"
              />
            </Box>
          )}

          {/* Información del proyecto */}
          <Stack spacing={4}>
            <Flex align="center" justify="space-between" flexWrap="wrap" gap={2}>
              <Text
                fontSize="xs"
                fontWeight={700}
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="accent.default"
              >
                Proyecto destacado
              </Text>

              {/* Controles de navegación solo si hay más de 1 proyecto */}
              {!isLoading && total > 1 && (
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Proyecto anterior"
                    icon={<FiChevronLeft />}
                    size="sm"
                    variant="outline"
                    borderRadius="full"
                    onClick={handlePrev}
                  />
                  <Text fontSize="12px" color="text.secondary" px={1}>
                    {currentIndex + 1} / {total}
                  </Text>
                  <IconButton
                    aria-label="Proyecto siguiente"
                    icon={<FiChevronRight />}
                    size="sm"
                    variant="outline"
                    borderRadius="full"
                    onClick={handleNext}
                  />
                </HStack>
              )}
            </Flex>

            {isLoading || !activeProject ? (
              <Stack spacing={3}>
                <Skeleton height="36px" width="80%" />
                <Skeleton height="20px" width="100%" />
                <Skeleton height="20px" width="60%" />
              </Stack>
            ) : (
              <>
                <Heading as="h2" size="h2" fontSize={{ base: '22px', md: '28px' }}>
                  {activeProject.nombre}
                </Heading>
                <Text color="text.secondary" fontSize="15px" lineHeight={1.6}>
                  {activeProject.descripcion}
                </Text>
                <Stack direction="row" spacing={8} fontSize="sm" pt={2}>
                  {activeProject.empresa && (
                    <Box>
                      <Text color="text.secondary" fontSize="xs" fontWeight={500}>
                        Cliente
                      </Text>
                      <Text fontWeight={600} color="text.primary">
                        {activeProject.empresa}
                      </Text>
                    </Box>
                  )}
                  {activeProject.estado && (
                    <Box>
                      <Text color="text.secondary" fontSize="xs" fontWeight={500}>
                        Estado
                      </Text>
                      <Text fontWeight={600} color="text.primary">
                        {activeProject.estado}
                      </Text>
                    </Box>
                  )}
                  {activeProject.ubicacion && (
                    <Box>
                      <Text color="text.secondary" fontSize="xs" fontWeight={500}>
                        Ubicación
                      </Text>
                      <Text fontWeight={600} color="text.primary">
                        {activeProject.ubicacion}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </>
            )}

            <Button
              as={RouterLink}
              to="/projects"
              variant="primary"
              alignSelf="flex-start"
              mt={2}
            >
              Ver casos de éxito
            </Button>
          </Stack>
        </SimpleGrid>
      </Container>
    </Box>
  );
}
